// src/components/NavbarComponent.js
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Nav, Container } from 'react-bootstrap';
import { AuthContext } from '../Auth/AuthContext';
import { React, useEffect, useState, useContext } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { useAdminCheck } from '../hooks/useAdminCheck';
import { db } from '../firebase/firebaseConfig';
import { useLanguage } from '../contexts/LanguageContext';
import './NavbarComponent.css';

const NavbarComponent = () => {
  const { user, logout } = useContext(AuthContext);
  const { isAdmin, isDelivery, loading, userId } = useAdminCheck();
  const { t } = useLanguage();
  const [cartCount, setCartCount] = useState(0);

  const [menuExpanded, setMenuExpanded] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Define page titles for different routes
  const getPageTitle = () => {
    const path = location.pathname;
    switch (path) {
      case '/cart':
        return t('nav.cart') || 'My Cart';
      case '/orders':
        return t('nav.myOrders') || 'My Orders';
      case '/checkout':
        return 'Checkout';
      case '/settings':
        return t('nav.settings') || 'Settings';
      case '/about':
        return t('nav.about') || 'About';
      case '/adminupload':
        return 'Upload Product';
      case '/adminorders':
        return 'Manage Orders';
      case '/login':
        return 'Login';
      case '/profile':
        return 'Profile';
      default:
        if (path.startsWith('/category/')) {
          const category = path.split('/')[2];
          return category ? category.replace(/_/g, ' ') : 'Category';
        }
        if (path.startsWith('/product/')) {
          return 'Product Details';
        }
        return null; // Home page or unknown route
    }
  };

  const isHomePage = location.pathname === '/' || location.pathname === '/home';
  const pageTitle = getPageTitle();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      alert("Logout failed");
    }
  };

    useEffect(() => {

    if (!user) {
      setCartCount(0);
      return;
    }

    const unsub = onSnapshot(doc(db, 'incart', user.uid), (docSnap) => {
    const itemsObj = docSnap.exists() ? docSnap.data().items || {} : {};
    const total = Object.values(itemsObj).reduce((acc, item) => acc + (item.qty || 1), 0);
      setCartCount(total);
    });

    return () => unsub();
  }, [user]);

  return (

    <Navbar 
      expand="lg" 
      className="shadow-sm forfeeder-navbar" 
      expanded={menuExpanded}
      onToggle={setMenuExpanded}
    >
      <Container>
        {isHomePage ? (
          // Home page: Show ForFeeder branding
          <Navbar.Brand as={Link} to="/" className="fw-bold d-flex align-items-center">
            <img
              src="/logo192.png"
              alt="Logo"
              width="30"
              height="30"
              className="d-inline-block align-top me-2"
            />
            ForFeeder
          </Navbar.Brand>
        ) : (
          // Other pages: Show back button and page title
          <div className="d-flex align-items-center">
            <button 
              className="page-nav-back-btn"
              onClick={() => navigate(-1)}
            >
              ←
            </button>
            <span className="page-nav-title">
              {pageTitle}
            </span>
          </div>
        )}


        <Navbar.Toggle 
          aria-controls="basic-navbar-nav"
          onClick={() => setMenuExpanded(!menuExpanded)}
        />
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="ms-auto">
            
            <Nav.Link 
              as={Link} 
              to="/"
              onClick={() => setMenuExpanded(false)}
            >
              🏠 {t('nav.home')}
            </Nav.Link>
            <Nav.Link 
              as={Link} 
              to="/cart"
              onClick={() => setMenuExpanded(false)}
            >
              🛒 {t('nav.cart')} ({cartCount})
            </Nav.Link>
            <Nav.Link 
              as={Link} 
              to="/orders"
              onClick={() => setMenuExpanded(false)}
            >
              📦 {t('nav.myOrders')}
            </Nav.Link>
            <Nav.Link 
              as={Link} 
              to="/settings"
              onClick={() => setMenuExpanded(false)}
            >
              ⚙️ {t('nav.settings')}
            </Nav.Link>
            <Nav.Link 
              as={Link} 
              to="/about"
              onClick={() => setMenuExpanded(false)}
            >
              ℹ️ {t('nav.about')}
            </Nav.Link>
            {isAdmin && (
              <Nav.Link 
                as={Link} 
                to="/adminupload"
                onClick={() => setMenuExpanded(false)}
              >
                {t('nav.uploadProduct')}
              </Nav.Link>
            )}
            {isDelivery && (
              <Nav.Link 
                as={Link} 
                to="/delivery-dashboard"
                onClick={() => setMenuExpanded(false)}
              >
                🚚 Delivery Dashboard
              </Nav.Link>
            )}
            {isAdmin && (
              <Nav.Link 
                as={Link} 
                to="/adminorders"
                onClick={() => setMenuExpanded(false)}
              >
                {t('nav.finishDelivery')}
              </Nav.Link>
            )}

            {user ? (
              <Nav.Link 
                onClick={() => {
                  setMenuExpanded(false);
                  handleLogout();
                }}
              >
                🚪 {t('nav.logout')}
              </Nav.Link>
            ) : (
              <Nav.Link 
                as={Link} 
                to="/login"
                onClick={() => setMenuExpanded(false)}
              >
                🔐 {t('nav.login')}
              </Nav.Link>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default NavbarComponent;
